/**
 * AppearanceSection - Unified display mode and theme selection
 * Design: Segmented control + theme card grid
 *
 * Syncs with Redux (pageLayoutSettingsSlice) and localStorage
 * to match AppearanceHub behavior — changes propagate globally.
 */
import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import axios from 'axios';
import { setTheme } from '../../../slices/pageLayoutSettingsSlice.mjs';

const DISPLAY_MODES = [
    { id: 'light', icon: '☀️', label: 'Light' },
    { id: 'dark', icon: '🌙', label: 'Dark' },
    { id: 'system', icon: '🖥️', label: 'System' },
];

const THEMES = [
    { id: 'default', icon: '🏀', name: 'Default', colors: ['#3b82f6', '#1e40af'] },
    { id: 'court-side', icon: '🎾', name: 'Court Side', colors: ['#10b981', '#047857'] },
    { id: 'full-court-press', icon: '🏈', name: 'Full Court', colors: ['#f59e0b', '#d97706'] },
    { id: 'hardwood', icon: '🪵', name: 'Hardwood', colors: ['#78350f', '#451a03'] },
    { id: 'triple-double', icon: '✨', name: 'Triple Double', colors: ['#8b5cf6', '#5b21b6'] },
    { id: 'downtown', icon: '🏙️', name: 'Downtown', colors: ['#6366f1', '#4338ca'] },
    { id: 'buzzer-beater', icon: '🚨', name: 'Buzzer Beater', colors: ['#ef4444', '#b91c1c'] },
];

export default function AppearanceSection({ preferences, onPreferenceChange, disabled }) {
    const dispatch = useDispatch();
    // Initialize with prop value only — no localStorage in initializer to avoid SSR hydration mismatch.
    // The useEffect below syncs from localStorage + props after mount.
    const [displayMode, setDisplayMode] = useState(preferences?.displayMode || 'system');
    const [selectedTheme, setSelectedTheme] = useState(preferences?.selectedTheme || 'default');
    const [saving, setSaving] = useState(false);

    // Sync from localStorage (client-only) + props
    useEffect(() => {
        // Read localStorage only on mount to avoid SSR mismatch
        if (typeof window !== 'undefined') {
            const savedMode = localStorage.getItem('displayMode');
            if (savedMode && ['light', 'dark', 'system'].includes(savedMode)) {
                setDisplayMode(savedMode);
            }
        }
    }, []); // Run once on mount

    // Sync with prop changes (after initial mount)
    useEffect(() => {
        if (preferences?.displayMode) {
            setDisplayMode(preferences.displayMode);
        }
        if (preferences?.selectedTheme) {
            setSelectedTheme(preferences.selectedTheme);
        }
    }, [preferences]);

    const handleDisplayModeChange = async (mode) => {
        setDisplayMode(mode);

        // Sync with localStorage (matching AppearanceHub)
        if (typeof window !== 'undefined') {
            localStorage.setItem('displayMode', mode);
        }

        // Apply immediately to document
        if (mode === 'system') {
            const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
            document.documentElement.classList.toggle('dark', prefersDark);
        } else {
            document.documentElement.classList.toggle('dark', mode === 'dark');
        }

        // Save to backend
        setSaving(true);
        try {
            // Map mode to colorScheme for API (matching AppearanceHub)
            let colorScheme = mode;
            if (mode === 'system') {
                colorScheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
            }
            await axios.patch('/q/api/user-preferences', { colorScheme, displayMode: mode });
            onPreferenceChange?.({ type: 'success', text: 'Display mode updated' });
        } catch (error) {
            onPreferenceChange?.({ type: 'error', text: 'Failed to save display mode' });
        } finally {
            setSaving(false);
        }
    };

    const handleThemeChange = async (themeId) => {
        setSelectedTheme(themeId);

        // Dispatch to Redux (matching AppearanceHub)
        dispatch(setTheme({ name: themeId, version: null }));

        // Apply theme immediately — remove attribute for 'default' (matching AppearanceHub)
        if (typeof document !== 'undefined') {
            if (themeId === 'default') {
                document.documentElement.removeAttribute('data-theme');
            } else {
                document.documentElement.setAttribute('data-theme', themeId);
            }
        }

        // Save to localStorage as fallback
        if (typeof localStorage !== 'undefined') {
            localStorage.setItem('selectedTheme', themeId);
        }

        // Save to backend
        setSaving(true);
        try {
            await axios.patch('/q/api/user-preferences', {
                selectedTheme: themeId === 'default' ? null : themeId
            });
            onPreferenceChange?.({ type: 'success', text: 'Theme updated' });
        } catch (error) {
            onPreferenceChange?.({ type: 'error', text: 'Failed to save theme' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <section id="appearance" className="settings-section">
            <div className="settings-section-header">
                <span className="settings-section-icon">🎨</span>
                <h2 className="settings-section-title">Appearance</h2>
            </div>
            <p className="settings-section-description">
                Customize how AllWrite looks and feels. Changes are applied immediately.
            </p>

            {/* Display Mode Segmented Control */}
            <div className="appearance-subsection">
                <h4 className="appearance-subsection-title">Display Mode</h4>
                <div className="display-mode-control" role="radiogroup" aria-label="Display mode">
                    {DISPLAY_MODES.map((mode) => (
                        <button
                            key={mode.id}
                            className={`display-mode-option ${displayMode === mode.id ? 'active' : ''}`}
                            onClick={() => handleDisplayModeChange(mode.id)}
                            disabled={disabled || saving}
                            role="radio"
                            aria-checked={displayMode === mode.id}
                        >
                            <span className="display-mode-icon">{mode.icon}</span>
                            <span>{mode.label}</span>
                        </button>
                    ))}
                </div>
            </div>

            {/* Theme Grid */}
            <div className="appearance-subsection">
                <h4 className="appearance-subsection-title">Theme</h4>
                <div className="theme-grid" role="radiogroup" aria-label="Theme selection">
                    {THEMES.map((theme) => (
                        <button
                            key={theme.id}
                            className={`theme-card ${selectedTheme === theme.id ? 'selected' : ''}`}
                            onClick={() => handleThemeChange(theme.id)}
                            disabled={disabled || saving}
                            role="radio"
                            aria-checked={selectedTheme === theme.id}
                        >
                            <span className="theme-card-check">✓</span>
                            <div
                                className="theme-card-preview"
                                style={{
                                    background: `linear-gradient(135deg, ${theme.colors[0]}, ${theme.colors[1]})`
                                }}
                            >
                                {theme.icon}
                            </div>
                            <div className="theme-card-name">{theme.name}</div>
                        </button>
                    ))}
                </div>
            </div>
        </section>
    );
}
